import { Injectable, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Leg } from '@approot/models';
import { PricingExtraService } from '@approot/services';
import { Utilities } from '@approot/utilities';
import { getAllAttributes } from '../utilities/leg-attribute-utilities';
import { optionsChangeHandler } from '../utilities/leg.utilities';
import { LegCurrencyService } from './leg-currency.service';
import { LegHelperService } from './leg-helper.service';
import { LegSpotStrikeService } from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegCurrencyListenersService {
  constructor(
    private pricingExtraService: PricingExtraService,
    private legHelperService: LegHelperService,
    private renderer: Renderer2,
    private legCurrencyService: LegCurrencyService,
    private spotStrikeService: LegSpotStrikeService
  ) {}

  public currencyListeners(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    this.quantoCurrencyListener(form, leg);
    this.normalCurrencyListener(form);
    this.genericCurrencyListener('currency1', 'notional1');
    this.genericCurrencyListener('currency2', 'notional2');
    this.genericCurrencyListener('secondCurrency', 'secondNotional');
    this.genericCurrencyListener('premiumCurrency', 'premium');
    this.genericCurrencyListener('midPvCurrency', 'midPv');
    this.genericCurrencyListener(
      'valuationAdjustmentCurrency',
      'valuationAdjustment'
    );
    this.genericCurrencyListener('capLossCurrency', 'capLossAmount');
    this.genericCurrencyListener(
      'fixedAmountCurrency',
      'fixedAmountLowerEkiZone'
    );
    this.genericCurrencyListener(
      'fixedAmountCurrency',
      'fixedAmountUpperEkiZone'
    );
    this.genericCurrencyListener('putCurrency', 'putAmount');
    this.genericCurrencyListener('instalmentCurrency', 'instalmentAmount');
    this.depositCurrencyListener(form, leg);
    this.alternativeCurrencyListeners(form);
    this.currencyAttributeListener(leg);
  }

  private currencyAttributeListener(leg: Leg): void {
    getAllAttributes(leg.attributesAsFormControls).forEach(attr => {
      if (attr.currencyAttribute) {
        this.genericCurrencyListener(attr.currencyAttribute, attr.key);
      }
    });
  }

  private quantoCurrencyListener(form: FormGroup, leg: Leg): void {
    if (form.get('quantoCurrency')) {
      optionsChangeHandler(
        form,
        leg,
        'quantoCurrency',
        [...this.pricingExtraService.currencies],
        ''
      );

      this.legHelperService.subSink.sink = form
        .get('quantoCurrency')
        ?.valueChanges.subscribe(val => {
          // add the chosen currency to the textBox
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + 'notional'),
            form.get('notional'),
            ` ${val}`,
            !!val,
            'currency',
            this.renderer
          );
        });
    }
  }

  private normalCurrencyListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('currency')
      ?.valueChanges.subscribe(val => {
        this.legCurrencyService.onCurrencyChange(val);
      });
  }

  private genericCurrencyListener(currency: string, amount: string) {
    // simply when currency changes, removes/adds the currency to the amount textbox
    const form = this.legHelperService.form;
    this.legHelperService.subSink.sink = form
      .get(currency)
      ?.valueChanges.subscribe(val => {
        if (val) {
          if (form.get(amount)) {
            Utilities.endOfTextBoxAdder(
              this.legHelperService.qs('#' + amount),
              form.get(amount),
              ' ' + val,
              !!val,
              'currency',
              this.renderer
            );
          }
        } else {
          if (form.get(amount)) {
            // add the chosen currency to the textbox
            Utilities.endOfTextBoxAdder(
              this.legHelperService.qs('#' + amount),
              form.get(amount),
              ' XXX',
              false,
              'currency',
              this.renderer
            );
          }
        }
      });
  }

  private depositCurrencyListener(form: FormGroup, leg: Leg): void {
    if (
      !leg.preSetPosition &&
      form.get('depositDate') &&
      form.get('depositCurrency')
    ) {
      form.get('depositDate').disable(); // disable the date
    }

    this.legHelperService.subSink.sink = form
      .get('depositCurrency')
      ?.valueChanges.subscribe(val => {
        this.legCurrencyService.onDepositCurrencyChange(val);
      });
  }

  private alternativeCurrencyListeners(form: FormGroup): void {
    const spotCtrl = form.get('spot');
    this.legHelperService.subSink.sink = form
      .get('alternativeCurrency')
      ?.valueChanges.subscribe(val => {
        this.legCurrencyService.onAlternativeCurrencyChange(
          val,
          this.legHelperService.legSyncingInfo,
          this.legHelperService.legIndex,
          this.spotStrikeService.hasSpotAlreadyLoaded,
          spotCtrl
        );
      });
  }
}
